import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import i18n from 'i18next';

import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

import { Button } from 'uikit/buttons';
import { Card, CardMenu } from 'uikit/cards';
import { Skeleton } from 'uikit/loaders';
import { Typo } from 'uikit/content';

import getUpNextCoverArt from '../helpers/getUpNextCoverArt';

import CatalogCarousel from './catalogCarousel';

import styles from '../styles/catalogCards.module.scss';

const CatalogCards = forwardRef(
  (
    {
      title,
      data,
      getMenuItems = () => null,
      isLoading = false,
      layoutMaxItems = 4,
      overflow = 'stack',
      viewAllLink = '/student/my_learning/up_next',
    },
    ref,
  ) => {
    const Element = overflow === 'scroll' ? CatalogCarousel : 'div';

    return (
      <div
        className={classnames(styles.upNext, { [styles.upNextStacked]: overflow === 'stack' })}
        ref={ref}
      >
        {overflow === 'stack' && (
          <Typo className={styles.title} element="h4" variant="d02" modifier="bold">
            {title}
          </Typo>
        )}
        <Element
          className={classnames({ [styles.isStacked]: overflow === 'stack' })}
          contentContainerClassName={classnames(styles.carouselContentContainer, {
            [styles.contentContainerSkeleton]: isLoading,
          })}
          hasOnlyOneItem={data?.length === 1}
          layoutMaxItems={layoutMaxItems}
          title={title}
        >
          {isLoading
            ? [...Array(layoutMaxItems)].map(() => (
                <li className={classnames(styles.cardItem, styles[`layout-${layoutMaxItems}`])}>
                  <Skeleton className={styles.loaderImage} height={170} width="100%" />
                  <div className={styles.content}>
                    <div className={styles.info}>
                      <Skeleton className={styles.loaderText} height={20} width="100%" />
                      <Skeleton className={styles.loaderText} height={20} width="100%" />
                    </div>
                    <div>
                      <Skeleton className={styles.loaderText} height={20} width={200} />
                    </div>
                  </div>
                </li>
              ))
            : data.map(
                ({
                  course_url: courseUrl,
                  cover_art: coverArt,
                  enrollment_metadata: enrollmentMetadata,
                  event_is_live: eventIsLive,
                  event_starts_at: eventStartsAt,
                  name,
                  description,
                  duration,
                  id,
                  is_favorite: isFavorite,
                  subtype: courseSubtype,
                  type: courseType,
                }) => {
                  const transformedCoverArt = getUpNextCoverArt({
                    coverArt,
                    courseType,
                    courseSubtype,
                  });

                  function formatDate({ dateString, isOverdue = false }) {
                    const date = new Date(dateString);

                    const options = isOverdue
                      ? { month: 'short', day: 'numeric', year: 'numeric' }
                      : {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          timeZoneName: 'short',
                          hour12: true,
                        };

                    return new Intl.DateTimeFormat('en-US', options).format(date);
                  }

                  const getDateMessage = () => {
                    if (eventStartsAt && !eventIsLive) {
                      return i18n.t('starts-on', {
                        date: formatDate({ dateString: eventStartsAt }),
                      });
                    }

                    if (enrollmentMetadata.due_on) {
                      if (enrollmentMetadata.status === 'overdue') {
                        return i18n.t('due-on-overdue', {
                          date: formatDate({
                            dateString: enrollmentMetadata.due_on,
                            isOverdue: true,
                          }),
                        });
                      }
                      return i18n.t('due-on', {
                        date: formatDate({ dateString: enrollmentMetadata.due_on }),
                      });
                    }

                    return '';
                  };

                  const {
                    relationship_reason: relationshipReason,
                    recommended_by_ai: recommendedByAi,
                  } = enrollmentMetadata;

                  const status =
                    enrollmentMetadata.flag_status === 'inProgress'
                      ? null
                      : enrollmentMetadata.flag_status;

                  return (
                    <div
                      className={classnames(styles.cardItem, styles[`layout-${layoutMaxItems}`])}
                      key={courseUrl}
                    >
                      <CardMenu
                        className={styles.cardMenu}
                        variant={layoutMaxItems === 5 ? 'list' : 'grid'}
                        menuItems={getMenuItems({
                          enrollmentId: enrollmentMetadata.id,
                          id: Number(id),
                          isFavorite,
                          isRequired: enrollmentMetadata.relationship_type === 'required',
                        })}
                      >
                        <Card
                          dateMessage={getDateMessage()}
                          duration={duration}
                          image={transformedCoverArt?.url?.w550}
                          isExternalLink
                          liveText={i18n.t('live-now')}
                          progress={enrollmentMetadata.progress || null}
                          status={status}
                          statusCustomMessage={recommendedByAi ? relationshipReason : null}
                          to={courseUrl}
                          trackingId="Up Next Card"
                          type={eventIsLive ? 'live' : 'upnext'}
                          variant={layoutMaxItems === 5 ? 'list' : 'grid'}
                        >
                          <Card.ContentContainer variant="grid">
                            <Card.Content title={name} variant="list" description={description} />
                          </Card.ContentContainer>
                        </Card>
                      </CardMenu>
                    </div>
                  );
                },
              )}
        </Element>
        <div className={styles.viewAllContainer}>
          <Button
            text={i18n.t('view-all')}
            variant="soft"
            isExternalLink
            iconPosition="right"
            to={viewAllLink}
            icon={<KeyboardArrowRight />}
          />
        </div>
      </div>
  );
  },
);

CatalogCards.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      course_url: PropTypes.string.isRequired,
      enrollment_metadata: PropTypes.shape({}).isRequired,
      cover_art: PropTypes.shape({}),
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      duration: PropTypes.string,
      is_favorited: PropTypes.bool,
      type: PropTypes.string,
    }),
  ).isRequired,
  getMenuItems: PropTypes.func,
  isLoading: PropTypes.bool,
  layoutMaxItems: PropTypes.number,
  overflow: PropTypes.oneOf(['stack', 'scroll']),
  viewAllLink: PropTypes.string,
};

export default CatalogCards;
